import React, { useEffect, useState, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import withRouter from "components/Common/withRouter"
import { Link } from "react-router-dom"
import { Alert, Card, CardBody, Spinner } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { Text } from "./UsersTableCol"
import TableContainer from "../../components/Common/TableContainer"
import { clearErrors, deleteUser, getDomainsRequest, getUsersData, getCompaniesAll } from "../../store/actions"
import { formatDate, truncate } from "helpers/helper_funcations"
import { formatUserName } from "helpers/formatUserName"
import {
  TABLE_BADGE_STYLE,
  getMustReadBadge,
  getPortalAccessBadge,
} from "helpers/userTableBadges"
import ConfirmModal from "components/Common/ConfirmModal"
import moment from "moment"

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const UsersTable = props => {
  const {
    message,
    usersData,
    domains,
    getUserLoading,
    deleteUserLoading,
    confirmDeleted,
    pagination
  } = useSelector(state => state.Dashboard)
  const { user: loggedUser } = useSelector(state => state.Login)
  const { allCompaniesData } = useSelector(state => state.Company)
  const canManageUsersFully = [1, 2].includes(Number(loggedUser?.role))
  const dispatch = useDispatch()
  const hasInteracted = useRef(false);
  const [users, setUsers] = useState([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [domain, setDomain] = useState("")
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })

  const openConfirm = (id) => {
    setPendingDeleteId(id)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return
    dispatch(deleteUser(props.router.navigate, getUsersData, pendingDeleteId))
  }

  const handleCancelDelete = () => {
    setConfirmOpen(false)
    setPendingDeleteId(null)
  }

  const onSearch = (value) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setSearch(value)
  }

  const onDomainChange = (value) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setDomain(value || "")
  }

  const onPageChange = (nextPageIndex) => {
    hasInteracted.current = true;
    setPageIndex(nextPageIndex)
  }

  const onDateRangeChange = (dates = []) => {
    hasInteracted.current = true;
    setPageIndex(0);
    const [start, end] = dates || [];
    const startISO = start ? new Date(start).toISOString() : null;
    const endISO = end
      ? new Date(new Date(end).setHours(23, 59, 59, 999)).toISOString()
      : null;
    setDateRange({ startDate: startISO, endDate: endISO });
  };

  const onPageSizeChange = (nextSize) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setPageSize(nextSize)
  }

  useEffect(() => {
    if (canManageUsersFully) {
      dispatch(getCompaniesAll({ all: true }))
    } else {
      dispatch(getDomainsRequest())
    }
  }, [dispatch, canManageUsersFully])

  useEffect(() => {
    const t = setTimeout(() => dispatch(clearErrors()), 8000)
    return () => clearTimeout(t)
  }, [message])

  useEffect(() => {
    if (!hasInteracted.current) return;
    const params = {
      page: pageIndex + 1,
      limit: pageSize,
      search,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (domain) {
      if (canManageUsersFully && UUID_RE.test(domain)) params.companyId = domain;
      else params.domain = domain;
    }
    dispatch(getUsersData(params))
  }, [pageIndex, pageSize, search, domain, dateRange.startDate, dateRange.endDate, dispatch, canManageUsersFully])

  useEffect(() => {
    setUsers(usersData)
  }, [usersData])

  useEffect(() => {
    if (!deleteUserLoading && confirmDeleted && confirmOpen) {
      setConfirmOpen(false)
      setPendingDeleteId(null)
    }
  }, [deleteUserLoading, confirmDeleted])

  const companiesById = useMemo(() => {
    const map = new Map()
    ;(allCompaniesData || []).forEach((company) => {
      if (company?.id) map.set(company.id, company)
    })
    return map
  }, [allCompaniesData])

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: (r) =>
          formatUserName({ firstName: r.firstName, lastName: r.lastName }) || "-",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          const user = cellProps.row.original
          const accessBadge = getPortalAccessBadge(user, companiesById)
          const mustReadBadge = getMustReadBadge(user)

          return (
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span>
                {formatUserName({ firstName: user.firstName, lastName: user.lastName })}
              </span>

              {accessBadge && (
                <span
                  title={accessBadge.title}
                  style={{
                    ...TABLE_BADGE_STYLE,
                    backgroundColor: accessBadge.backgroundColor,
                  }}
                >
                  {truncate(accessBadge.label, 18)}
                </span>
              )}
              {mustReadBadge && (
                <span
                  title={mustReadBadge.title}
                  style={{
                    ...TABLE_BADGE_STYLE,
                    backgroundColor: mustReadBadge.backgroundColor,
                  }}
                >
                  {mustReadBadge.label}
                </span>
              )}
            </div>
          )
        },
      },
      {
        Header: "Email",
        accessor: "email",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => <Text {...cellProps} />,
      },
      {
        Header: "Resources",
        accessor: row => (Array.isArray(row?.documents) ? row.documents.length : 0),
        id: "documentsCount",
        disableFilters: true,
        filterable: false,
        sortType: "basic",
        Cell: cellProps => {
          const user = cellProps.row.original;
          const docs = Array.isArray(user?.documents) ? user.documents : []
          const count = docs.length;

          if (!count) return <span style={{ opacity: 0.7 }}>—</span>

          const preview = docs.slice(0, 3)
          return (
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span className="badge bg-light text-dark">{count}</span>
              {preview.map(d => (
                <span key={d.id} className="badge bg-secondary">
                  {truncate(d.name || d.fileName || d.id, 13)}
                </span>
              ))}
              {count > 3 && (
                <span className="badge bg-light text-dark">+{count - 3} more</span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Password Resets",
        accessor: "resetPassCount",
        disableFilters: true,
        filterable: false,
        sortType: "basic",
      },
      {
        Header: "Login Counts",
        accessor: "loginCount",
        disableFilters: true,
        filterable: false,
        sortType: "basic",
      },
      {
        Header: "Last Login",
        accessor: "lastLoginAt",
        disableFilters: true,
        filterable: false,
        sortType: "basic",
        Cell: ({ value }) => (
          <span title={value || ""}>
            {value ? moment(value).format("DD MMM YYYY, h:mm A") : "—"}
          </span>
        ),
      },
      {
        Header: "Created",
        accessor: "createdAt",
        disableFilters: true,
        Cell: ({ value }) => (
          <span>{value ? moment(value).format("DD/MM/YYYY") : "—"}</span>
        ),
      },
      {
        Header: "Action",
        disableFilters: true,
        Cell: ({ row }) => {
          const id = row.original.id
          const email = row.original.email
          return (
            <div className="flex-row" style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className={`download-rounded-btn ${deleteUserLoading || email === "superadmin@armada.com" ? "disabled" : ""}`}
                onClick={() => props.router.navigate(`/user/${id}`)}
                disabled={deleteUserLoading || email === "superadmin@armada.com"}
                title={deleteUserLoading ? "Please wait…" : "Edit"}
              >
                <i className="mdi mdi-pencil" style={{ color: "white", fontSize: 15 }} />
              </button>

              <button
                type="button"
                className="download-rounded-btn"
                onClick={() => openConfirm(id)}
                disabled={deleteUserLoading}
                title={deleteUserLoading ? "Deleting…" : "Delete"}
                style={{ border: "none" }}
              >
                <i className="mdi mdi-delete" style={{ color: "white", fontSize: 15 }} />
              </button>
            </div>
          )
        },
      },
    ],
    [companiesById, deleteUserLoading]
  )

  return (
    <React.Fragment>
      <div className="row-spaceBtwn align-items-center mb-2">
        <div className="h2 mb-0 d-flex align-items-center heading gap-2">Users
        </div>
        {/* <div onClick={() => dispatch(getUsersData())}>
          {!getUserLoading ? (
            <img
              src={require("../../assets/images/refresh.png")}
              height={23}
              className="cursor-pointer"
              style={{ cursor: "pointer" }}
              alt="refresh"
            />
          ) : (
            <img
              src={require("../../assets/images/refresh.gif")}
              height={23}
              className="cursor-pointer"
              style={{ cursor: "pointer" }}
              alt="refreshing"
            />
          )}
        </div> */}

        {/* <Link to="/add-user" className="add-new-user">+ Add user</Link> */}
      </div>
      <Card className="shadow-none mb-0">
        <CardBody style={{ background: "#F8F8FB" }} className="pb-0 px-0 px-sm-4">
          {!!message && <Alert color="danger" className="mb-4">
            {String(message)}
          </Alert>}
          <TableContainer
            columns={columns}
            data={users}
            isGlobalFilter={true}
            isJobListGlobalFilter={true}
            isAddOptions={false}
            isPagination={true}
            iscustomPageSizeOptions={false}
            customPageSize={10}
            totalPages={pagination.totalPages}
            manual
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            searchValue={search}
            onFilterChange={onDomainChange}
            filterValue={domain}
            filterData={canManageUsersFully ? (allCompaniesData || []) : domains}
            selectTitle={canManageUsersFully ? "Select Company" : "Select Domain"}
            loading={getUserLoading}
            onDateRangeChange={onDateRangeChange}
            dateRangeValue={dateRange}
            pagination="pagination pagination-rounded justify-content-end mb-2"
          />
        </CardBody>
        <ConfirmModal
          isOpen={confirmOpen}
          title="Confirm Delete"
          confirmLabel="Delete"
          confirmColor="danger"
          loading={deleteUserLoading}
          errorMessage={message || ""}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        >
          Are you sure you want to delete this user? This action cannot be undone.
        </ConfirmModal>
      </Card>
    </React.Fragment>
  )
}

UsersTable.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
}

export default withRouter(UsersTable)
